import React, { useContext, useEffect, useRef, useState } from "react";
import { AmountByDayOfDecimal, AmountByDayOfInteger, DayOfWeek, SchedulingTimeByDaySummary, TimeByDay } from "interfaces/Mueller.Business.Models";
import { getTimeLogByDaySummary } from "requests/schedulingTimeCard";
import { MCard, MSpinner } from "components/Shared";
import { getTooltipRef, TooltipContext } from "components/Shared/TooltipProvider";


interface Props {
  employeeName?: string,
  internetUserId: number;
  payPeriodId: number;
  refreshCard?: Date,
  onDateClicked: (date: Date) => void,
};

const daysOrder = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];

const SchedulingAssistantSummaryCard = ({
  employeeName,
  internetUserId,
  payPeriodId,
  refreshCard,
  onDateClicked,
}: Props) => {
  const [timelogData, setTimelogData] = useState<SchedulingTimeByDaySummary>();
  const [timeEfficiencyByDay, setTimeEfficiencyByDay] =  useState<ValueByDay[]>();
  const [dayOfWeekMousedOver, setDayOfWeekMousedOver] = useState<string>(null);
  
  const mounted = useRef(true);

  useEffect(() => {
    if (payPeriodId) getTimeLogData();
  }, [refreshCard, internetUserId, payPeriodId]);

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  const percentString = (dividend: number, divisor: number) => `${divisor === 0 ? 0 : Math.round(dividend / divisor * 100)}%`;

  const setTimelogDataWrapped = (data: SchedulingTimeByDaySummary) => {
    setTimelogData(data);
    setTimeEfficiencyByDay(daysOrder.map(d => {
      let total = data.expectedHours.timeByDayList.find(t => t.dayOfWeek === d);
      let expected = data.timeClockDailyHours.timeByDayList.find(t => t.dayOfWeek === d);

      return {
        dayOfWeek: d,
        value: percentString(total.hours, expected.hours),
      } as ValueByDay;
    }));
  };

  const numberToStringHundredths = (number: number) => number.toFixed(2);
  const numberToStringWhole = (number: number) => number.toFixed(0);

  //@ts-ignore
  const mapTimeByDayEntry = (tbd: TimeByDay): ValueByDay => ({ dayOfWeek: tbd.dayOfWeek, value: numberToStringHundredths(tbd.hours), date: tbd.date });
  //@ts-ignore
  const mapAmountByDayEntry = (abd: AmountByDayOfInteger | AmountByDayOfDecimal): ValueByDay => ({ dayOfWeek: abd.dayOfWeek, value: numberToStringWhole(abd.amount), date: abd.date });

  const getTimeLogData = () => {
    setTimelogData(null);
    getTimeLogByDaySummary(internetUserId, payPeriodId, setTimelogDataWrapped, mounted);
  };

  if (!timelogData) return <MSpinner />;

  timelogData.timeClockDailyHours.timeByDayList[0].dayOfWeek

  const tableRef = (element: HTMLDivElement) => {
    if (element) {
      element.addEventListener("mouseover",
        (event: MouseEvent) => {
          // @ts-ignore yes getAttribute does exist.... promise
          let dayOfWeek = event.target.getAttribute("data-day-of-week");
          if (dayOfWeek) {
            setDayOfWeekMousedOver(dayOfWeek);
          }
        }
      );
      element.addEventListener("mouseout",
        (event: MouseEvent) => {
          setDayOfWeekMousedOver(null);
        }
      );
    }
  };

  const summaryProps = {
    onClick: onDateClicked,
    dayOfWeekMousedOver: parseInt(dayOfWeekMousedOver),
  } as SummaryRowProps;

  return <MCard title="Summary">
    <div className="qa-table-employee-timelogs summary-time-logs" style={{ padding: 0 }}>
      <table ref={tableRef}>
        <thead>
          <tr>
            <th className="qa-row" style={{ width: 180 }}>{employeeName ?? ""}</th>
            <th className="qa-col">Mon</th>
            <th className="qa-col">Tue</th>
            <th className="qa-col">Wed</th>
            <th className="qa-col">Thu</th>
            <th className="qa-col">Fri</th>
            <th className="qa-col">Sat</th>
            <th className="qa-col">Sun</th>
            <th className="qa-col">Total</th>
          </tr>
        </thead>
        <tbody>
          <SummaryRow title="Clock In Hours" valueByDayList={timelogData.timeClockDailyHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.timeClockHoursTotal)} {...summaryProps} />
          <SummaryRow title="Total Expected Hours" titleTooltip="Daily total of (Total Expected Minutes of all cases submitted and scheduled) / 60" valueByDayList={timelogData.expectedHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.expectedHoursTotal)} {...summaryProps} />
          <SummaryRow title="Total Appointment Hours Expected" titleTooltip="Daily total of (Sum of Number of Cases Scheduled X Expected Minutes) / 60" valueByDayList={timelogData.scheduledExpectedHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.scheduledExpectedHoursTotal)} {...summaryProps} />
          <SummaryRow title="Total QA Submission Hours Expected" titleTooltip="Daily total of (Sum of Number of Cases Submitted to QA X Expected Minutes) / 60" valueByDayList={timelogData.submittedExpectedHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.submittedExpectedHoursTotal)} {...summaryProps} />
          <SummaryRow title="Total CS Note Completion Hours Expected" titleTooltip="(Sum of Number CS Notes Completed x Expected Minutes) / 60 )" valueByDayList={timelogData.incidentExpectedHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.incidentExpectedHoursTotal)} {...summaryProps} />
          <SummaryRow title="Total Manual Order Hours Expected" titleTooltip="Expected hours of user manually ordering a case" valueByDayList={timelogData.webOrderExpectedHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.webOrderExpectedHoursTotal)} {...summaryProps} />
          <SummaryRow title="Total Coaching Call Hours Expected" titleTooltip="(Sum of Coaching Calls X 30) / 60" valueByDayList={timelogData.coachingCallExpectedHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.coachingCallExpectedHoursTotal)} {...summaryProps} />
          <SummaryRow title="Total Call Hours" titleTooltip="(Sum of Call Hours) / 60" valueByDayList={timelogData.additionalCallExpectedHours.timeByDayList.map(mapTimeByDayEntry)} total={numberToStringHundredths(timelogData.additionalCallExpectedHoursTotal)} {...summaryProps} />
          <SummaryRow title="Time Efficiency" titleTooltip="Total Expected Hours/Clock In Hours %" valueByDayList={timeEfficiencyByDay} total={percentString(timelogData.expectedHoursTotal, timelogData.timeClockHoursTotal)} {...summaryProps} />
          <SummaryRow title="QA" titleTooltip="Daily Count of SA Submitted to QA Completed cases" valueByDayList={timelogData.submittedCount.amountByDayList.map(mapAmountByDayEntry)} total={numberToStringWhole(timelogData.submittedCountTotal)} {...summaryProps} />
          <SummaryRow title="Appt" titleTooltip="Daily Count of SA Scheduled Completed cases" valueByDayList={timelogData.scheduledCount.amountByDayList.map(mapAmountByDayEntry)} total={numberToStringWhole(timelogData.scheduledCountTotal)} {...summaryProps} />
          <SummaryRow title="CS" titleTooltip="Daily Count of SA Customer Service Completed incidents" valueByDayList={timelogData.incidentCount.amountByDayList.map(mapAmountByDayEntry)} total={numberToStringWhole(timelogData.incidentCountTotal)} {...summaryProps} />
        </tbody>
      </table>
    </div>
  </MCard>;
};

interface ValueByDay {
  dayOfWeek: DayOfWeek,
  value: string,
  date: Date,
};

interface SummaryRowProps {
  title: string,
  valueByDayList: ValueByDay[],
  total: string,
  titleTooltip?: string,
  onClick: (date: Date) => void,
  dayOfWeekMousedOver: DayOfWeek,
};

const SummaryRow = ({ title, valueByDayList, total, titleTooltip, onClick, dayOfWeekMousedOver }: SummaryRowProps) => {
  const tooltipContext = useContext(TooltipContext);

  const tooltipRef = titleTooltip ? getTooltipRef(titleTooltip, tooltipContext) : undefined;

  return <tr>
    <th className="qa-row" ref={tooltipRef}>{title}</th>
    {daysOrder.map(d => {
      let time = valueByDayList?.find(t => t.dayOfWeek === d);
      return <td key={d.toString()} data-day-of-week={d.toString()} className={dayOfWeekMousedOver === d ? "hover" : ""} onClick={() => onClick(time.date)} >{time?.value}</td>;
    })}
    <td>{total}</td>
  </tr>
};

export default SchedulingAssistantSummaryCard;
